import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EASE_OUT } from '@/lib/motion';

export interface StepCardProps {
  stepNumber: number;
  title: string;
  disabled?: boolean;
  onPreview?: () => void;
  onCancel?: () => void;
  onSave?: () => void;
  saving?: boolean;
  saved?: boolean;
  /** Optional recap rendered under the title in the collapsed bar (e.g. "Onboarding · 30 min") */
  summary?: React.ReactNode;
  /** Stagger delay (seconds) for the entrance animation. Default 0. */
  entranceDelay?: number;
  children: React.ReactNode;
}

/** Check icon for Save button */
function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** X icon for Cancel button */
function XIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

/** Eye icon for Preview button */
function EyeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

/** Pen icon for Edit button */
function PenIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M15.232 5.232l3.536 3.536M16.707 3.293a2 2 0 012.828 0l1.172 1.172a2 2 0 010 2.828L7.5 20.5H3v-4.5L16.707 3.293z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Large checkmark for the collapsed completed badge */
function CompletedCheckIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="21" viewBox="0 0 28 21" fill="none">
      <path
        d="M10.1333 20.2667C9.65414 20.2652 9.19708 20.0702 8.87122 19.7282L0.468651 11.0285C-0.185427 10.35 -0.150594 9.28398 0.546453 8.64738C1.2435 8.01078 2.3388 8.04468 2.99288 8.7231L10.1161 16.1103L24.6563 0.629148C25.0584 0.141853 25.7048 -0.0884252 26.3351 0.0311175C26.9653 0.15066 27.4756 0.600329 27.6603 1.19891C27.8449 1.79748 27.6735 2.44635 27.2151 2.88401L11.4127 19.7114C11.0899 20.0596 10.6325 20.2608 10.1506 20.2667H10.1333Z"
        style={{ fill: 'rgb(var(--color-primary))' }}
      />
    </svg>
  );
}

export default function StepCard({
  stepNumber,
  title,
  disabled = false,
  onPreview,
  onCancel,
  onSave,
  saving = false,
  saved = false,
  summary,
  entranceDelay = 0,
  children,
}: StepCardProps) {
  const [isEditing, setIsEditing] = useState(false);

  // "Saved" is a transient confirmation, not a resting label: it shows for a
  // moment right after a save completes, then the button reads "Save" again
  // (the `saved` prop stays true forever, so labelling from it directly would
  // leave every revisited step stuck in the past tense).
  const [justSaved, setJustSaved] = useState(false);
  const prevSavedRef = useRef(saved);
  useEffect(() => {
    const wasSaved = prevSavedRef.current;
    prevSavedRef.current = saved;
    if (saved && !wasSaved) {
      setJustSaved(true);
      const timeout = setTimeout(() => setJustSaved(false), 2000);
      return () => clearTimeout(timeout);
    }
  }, [saved]);

  // Show collapsed bar when saved and not actively editing
  const isCollapsed = saved && !isEditing;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: disabled ? 0.45 : 1, y: 0 }}
      transition={{ duration: 0.25, ease: EASE_OUT, delay: entranceDelay }}
      className="relative rounded-[22px] p-[10px]"
      style={{
        background: 'linear-gradient(to right, rgb(var(--color-primary) / 0.2), rgb(var(--color-secondary) / 0.2))',
        pointerEvents: disabled ? 'none' : 'auto',
      }}
      aria-disabled={disabled}
    >
      <AnimatePresence mode="wait" initial={false}>
        {isCollapsed ? (
          /* ── Collapsed completed bar ── */
          <motion.div
            key="collapsed"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18, ease: EASE_OUT }}
            className="overflow-hidden"
          >
            <div className="bg-surface-elevated rounded-2xl shadow-[0px_4px_20px_0px_rgba(0,0,0,0.08)] px-4 py-4 flex items-center gap-4">
              {/* Checkmark badge */}
              <div className="shrink-0 w-[64px] h-[64px] rounded-full border-2 border-grey-500/15 flex items-center justify-center">
                <div className="bg-surface-elevated shrink-0 w-[45px] h-[45px] rounded-full flex items-center justify-center drop-shadow-[0_0_3px_rgba(0,0,0,0.21)] dark:drop-shadow-[0_0_3px_rgba(255,255,255,0.21)]">
                  <CompletedCheckIcon />
                </div>
              </div>

              {/* Title + optional summary recap */}
              <div className="flex-1 min-w-0">
                <h4 className="text-2xl font-bold text-text-primary leading-9 truncate">
                  Step {stepNumber}: {title}
                </h4>
                {summary && (
                  <p className="text-sm text-text-secondary mt-0.5 truncate">
                    {summary}
                  </p>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-3 shrink-0">
                {/* Preview — outlined; rendered only when onPreview is provided */}
                {onPreview && (
                  <button
                    type="button"
                    onClick={onPreview}
                    className="inline-flex items-center gap-1.5 rounded-full border border-text-primary text-text-primary px-3 py-1.5 text-sm font-bold hover:bg-text-primary/[0.06] active:scale-[0.97] transition-[background-color,transform] duration-150"
                  >
                    <EyeIcon />
                    Preview
                  </button>
                )}

                {/* Edit — purple gradient */}
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center gap-1.5 rounded-full text-white px-4 py-1.5 text-sm font-bold min-w-[100px] justify-center hover:brightness-105 active:scale-[0.97] transition-[filter,transform] duration-150"
                  style={{ background: `linear-gradient(to top, ${'rgb(var(--color-primary-700))'}, rgb(var(--color-primary)) 70%)` }}
                >
                  <PenIcon />
                  Edit
                </button>
              </div>
            </div>
          </motion.div>
        ) : (
          /* ── Expanded full step ── */
          <motion.div
            key="expanded"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18, ease: EASE_OUT }}
            className="overflow-hidden"
          >
            {/* White inner card */}
            <div className="bg-surface-elevated rounded-2xl shadow-[0px_0px_2px_0px_rgba(189,188,201,0.2),0px_12px_24px_-4px_rgba(189,188,201,0.12)] px-11 py-14">
              {/* Step header */}
              <div className="text-center mb-8">
                <h5 className="text-2xl font-bold text-text-primary leading-9">
                  Step {stepNumber}: {title}
                </h5>
              </div>

              {/* Content area */}
              <div>{children}</div>
            </div>

            {/* Footer — inside gradient area, below white card */}
            {(onPreview || onCancel || onSave || (saved && isEditing)) && (
              <div className="flex items-center justify-center gap-3 pt-4 pb-1.5 flex-wrap">
                {/* Preview button — dark outline (per Figma; Save is the only purple) */}
                {onPreview && (
                  <button
                    type="button"
                    onClick={onPreview}
                    className="inline-flex items-center gap-1.5 rounded-full border border-text-primary text-text-primary px-3 py-1.5 text-sm font-bold hover:bg-text-primary/[0.06] active:scale-[0.97] transition-[background-color,transform] duration-150"
                  >
                    <EyeIcon />
                    Preview
                  </button>
                )}

                {/* Cancel button — orange filled */}
                {(onCancel || (saved && isEditing)) && (
                  <button
                    type="button"
                    onClick={() => {
                      if (saved && isEditing) {
                        setIsEditing(false);
                      } else {
                        onCancel?.();
                      }
                    }}
                    className="inline-flex items-center gap-1.5 rounded-full bg-surface-primary border border-secondary-500/40 text-secondary-500 px-3 py-1.5 text-sm font-bold hover:bg-secondary-500/[0.08] active:scale-[0.97] transition-[background-color,transform] duration-150"
                  >
                    <XIcon />
                    Cancel
                  </button>
                )}

                {/* Save button — green/purple filled */}
                {onSave && (
                  <button
                    type="button"
                    onClick={() => {
                      onSave();
                      // Collapse after saving
                      if (!saving) {
                        setTimeout(() => setIsEditing(false), 300);
                      }
                    }}
                    disabled={saving}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-bold active:scale-[0.97] transition-[background-color,transform] duration-150 disabled:cursor-not-allowed disabled:active:scale-100 ${ saving ? 'bg-primary-500/60' : justSaved ? 'bg-success-500' : 'bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400' }`}
                  >
                    {saving ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                        className="animate-spin"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="9"
                          stroke="rgba(255,255,255,0.3)"
                          strokeWidth="3"
                        />
                        <path
                          d="M12 3a9 9 0 019 9"
                          stroke="#fff"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />
                      </svg>
                    ) : (
                      <CheckIcon />
                    )}
                    {saving ? 'Saving…' : justSaved ? 'Saved' : 'Save'}
                  </button>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
